import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription, Subject } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { TranslocoModule } from '@jsverse/transloco';
import { ToastService, Toast, ToastType } from '../../../services/toast.service';
import { ButtonComponent } from '../../button/button.component';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule, ButtonComponent, TranslocoModule],
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  animations: [
    trigger('toastAnimation', [
      state(
        'void',
        style({
          transform: 'translateX(100%)',
          opacity: 0,
        })
      ),
      state(
        'visible',
        style({
          transform: 'translateX(0)',
          opacity: 1,
        })
      ),
      transition('void => visible', [animate('300ms ease-out')]),
      transition('visible => void', [animate('200ms ease-in')]),
    ]),
  ],
})
export class ToastComponent implements OnInit, OnDestroy {
  toasts: Toast[] = [];
  private subscription: Subscription = new Subscription();
  private progressMap = new Map<string, number>();
  private destroy$ = new Subject<void>();
  private animationFrameIds = new Map<string, number>();

  constructor(private toastService: ToastService, private ngZone: NgZone) {}

  ngOnInit(): void {
    this.subscription = this.toastService.toasts$.subscribe((toasts) => {
      // Find new toasts that need progress tracking
      toasts.forEach(toast => {
        if (toast.autoClose && !this.progressMap.has(toast.id)) {
          this.progressMap.set(toast.id, 100);
          this.startProgressCountdown(toast);
        }
      });
      
      // Remove progress tracking for toasts that are no longer in the list
      const currentToastIds = new Set(toasts.map(t => t.id));
      Array.from(this.progressMap.keys()).forEach(id => {
        if (!currentToastIds.has(id)) {
          this.progressMap.delete(id);
          this.cancelProgressAnimation(id);
        }
      });
      
      this.toasts = toasts;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription.unsubscribe();
    
    // Cancel all animations
    this.animationFrameIds.forEach((id) => {
      cancelAnimationFrame(id);
    });
    this.animationFrameIds.clear();
  }

  /**
   * Close a toast by ID
   * @param id The ID of the toast to close
   */
  closeToast(id: string): void {
    this.toastService.remove(id);
  }
  
  /**
   * Start progress countdown for auto-closing toast using requestAnimationFrame
   */
  private startProgressCountdown(toast: Toast): void {
    if (!toast.autoClose) return;
    
    // Cancel any existing animation
    this.cancelProgressAnimation(toast.id);
    
    const duration = toast.duration || 5000;
    const startTime = performance.now();
    
    // Run animation outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      const animate = (timestamp: number) => {
        // Calculate progress based on elapsed time
        const elapsed = timestamp - startTime;
        const progressPercent = 100 - (elapsed / duration * 100);
        const newProgress = Math.max(0, progressPercent);
        
        // Update progress in Angular zone to trigger change detection
        this.ngZone.run(() => {
          if (newProgress <= 0) {
            this.progressMap.delete(toast.id);
            // No need to cancel animation as it will stop naturally
          } else {
            this.progressMap.set(toast.id, newProgress);
            // Continue the animation
            const animationId = requestAnimationFrame(animate);
            this.animationFrameIds.set(toast.id, animationId);
          }
        });
      };
      
      // Start the animation
      const animationId = requestAnimationFrame(animate);
      this.animationFrameIds.set(toast.id, animationId);
    });
  }
  
  /**
   * Get current progress percentage for a toast
   */
  getProgress(id: string): number {
    return this.progressMap.get(id) || 0;
  }

  /**
   * Get the icon class based on toast type
   * @param type The type of toast
   * @returns The CSS class for the icon
   */
  getIconClass(type: ToastType): string {
    switch (type) {
      case ToastType.ERROR:
        return 'toast-icon-error';
      case ToastType.WARNING:
        return 'toast-icon-warning';
      case ToastType.SUCCESS:
        return 'toast-icon-success';
      case ToastType.INFO:
        return 'toast-icon-info';
      default:
        return 'toast-icon-info';
    }
  }

  /**
   * Get CSS class for toast type
   */
  getToastClass(type: ToastType): string {
    return `toast-${type}`;
  }
  
  /**
   * Check if toast has progress bar
   */
  hasProgressBar(toast: Toast): boolean {
    return toast.autoClose === true;
  }
  

  
  /**
   * Cancel the progress animation for a toast
   */
  private cancelProgressAnimation(id: string): void {
    if (this.animationFrameIds.has(id)) {
      cancelAnimationFrame(this.animationFrameIds.get(id)!);
      this.animationFrameIds.delete(id);
    }
  }
}
